import { awaitableTemplate } from '../../../../webmodule-common/other/ui/template-processor.js';
import { customElement } from 'lit/decorators.js';
import { firstValidString } from '../../../../webmodule-common/other/ui/string-helper-functions.js';
import { html } from 'lit';
import { interpolateExternal } from '../../../../localization/global';
import { tryAsNumberString } from '../../../../webmodule-common/other/number-utilities.js';
import { ViewBase } from '../../../../webmodule-common/other/ui/view-base';

import type { EventTemplate } from '../../../../webmodule-common/other/ui/events.js';
import type { FranchiseeQuoteContainerManager } from '../data/franchisee-quote-manager';
import type { QCValue } from '../../../../webmodule-common/interop/webmodule-interop.js';
import type { QuoteContainerManager } from '../../../quotes/data/quote-container';
import type { TemplateResult } from 'lit';

export interface FranchiseeQuoteCalculationsViewOptions {
  quoteManager: QuoteContainerManager;
}

/**
 * The quote calculation values returned by V6, as their own page on the quote view.
 * Read-only: nothing here is edited, so the view carries no databinding and takes no
 * part in the save workflow.
 */
@customElement('wm-franchiseequotecalculationsview')
export class FranchiseeQuoteCalculationsView extends ViewBase {
  protected readonly quoteManager: QuoteContainerManager;

  constructor(options: FranchiseeQuoteCalculationsViewOptions) {
    super();

    this.quoteManager = options.quoteManager;
  }

  protected get qcm(): FranchiseeQuoteContainerManager {
    return this.quoteManager as FranchiseeQuoteContainerManager;
  }

  public async refreshData(): Promise<void> {
    this.requestUpdate();
  }

  protected template(): EventTemplate {
    const quoteCalculationsTemplate = awaitableTemplate(async (): Promise<TemplateResult[]> => {
      await this.quoteManager.needsQuoteItems();

      const qcResultTemplate = (item: QCValue) => html`
        <div class="quotecalculation-col">
          <div class="quotecalculation-col-name">${firstValidString(item.caption, item.name)}</div>
          <div class="quotecalculation-col-value">
            ${firstValidString(item.displayValue, tryAsNumberString(item.value, 2))}
          </div>
        </div>
      `;

      // quoteCalculationValues drops any group the supplier sent with no values, which would
      // otherwise render as a title bar over blank space. The report section suppresses the
      // same shape, so both surfaces show the same groups.
      return this.qcm.quoteCalculationValues.map(
        element => html`
          <div class="quotecalculation-section">
            <div class="quotecalculation-label">${interpolateExternal(element.title)}</div>
            <div class="quotecalculation-result">${element.values.map(x => qcResultTemplate(x))}</div>
          </div>
        `
      );
    });

    // One full-width column. The markup came from the items view, where it was the wide half of
    // the two-column `quote-price-summary` row - carrying those classes onto a dedicated tab
    // would cap the content at 55-70% width against a sibling column that no longer exists.
    // `.quotecalculation-*` styling is defined at the top level, so it is unaffected.
    return html`
      <form class="py-3 px-0 form-one-col">
        <div class="row">
          <div class="quote-calculations">${quoteCalculationsTemplate}</div>
        </div>
      </form>
    `;
  }
}
